import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
  Checkbox,
} from "@mui/material";
import {
  SearchIcon,
  PlusCircle,
  HelpCircle,
  RotateCw,
  X,
  Settings,
  FilterX,
  Plus,
} from "lucide-react";
import UnitTable, { defaultColumns } from "./Components/UnitTable";
import UnitFilters from "./Components/UnitFilters";
import BreadCrumbsContainer from "../../Common/Breadcrumbs";
import { UnitTypeModal } from "./Components/UnitComponents/UnitTypeModal";
import { errorToast, successToast } from "../../../services/toast.service";

const Units = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refetchFn, setRefetchFn] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(
    defaultColumns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}),
  );
  const [filters, setFilters] = useState({
    type: "ALL",
    category: "ALL",
    sortBy: "",
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const handleRefresh = async () => {
    if (refetchFn) {
      setIsRefreshing(true);
      try {
        await refetchFn();
        successToast("Units refreshed");
      } catch (error) {
        errorToast("Failed to refresh units");
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = columnId => {
    const column = defaultColumns.find(col => col.id === columnId);
    if (column?.required) return;

    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      type: "ALL",
      category: "ALL",
      sortBy: "",
    });
    setSearchQuery("");
  };

  const [selectedType, setSelectedType] = useState(null);

  return (
    <div className="bg-white h-[calc(100vh-120px)]">
      <BreadCrumbsContainer />
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Units
          </h1>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] flex items-center gap-2">
            <Plus size={16} />
            Add New Unit
          </button>
        </div>

        {/* Search and Settings Section */}
        <div className="flex items-center gap-4">
          {/* Search Bar */}
          <div className="w-[550px]">
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by name, abbreviation..."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                  <X size={16} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              onClick={handleRefresh}
              className="hover:bg-gray-100"
              disabled={isRefreshing}
              title="Refresh">
              <RotateCw
                size={20}
                className={`text-gray-600 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
            </IconButton>
            <IconButton
              onClick={handleSettingsClick}
              className="hover:bg-gray-100"
              title="Column Settings">
              <Settings size={20} className="text-gray-600" />
            </IconButton>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <UnitFilters filters={filters} onFilterChange={setFilters} />

          {(filters.type !== "ALL" ||
            filters.category !== "ALL" ||
            searchQuery) && (
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 
                       hover:bg-gray-100 rounded-lg transition-colors">
              <FilterX size={16} />
              Clear Filters
            </button>
          )}
        </div>

        <UnitTable
          searchQuery={debouncedSearchQuery}
          filters={filters}
          anchorEl={anchorEl}
          onCloseSettings={handleCloseSettings}
          visibleColumns={visibleColumns}
          onColumnToggle={handleColumnToggle}
          setRefetchFn={setRefetchFn}
        />
      </div>

      {/* Column Settings Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseSettings}
        PaperProps={{
          className: "mt-2",
          elevation: 0,
          sx: {
            border: "1px solid #E5E7EB",
            borderRadius: "0.5rem",
            minWidth: 180,
          },
        }}>
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-700">Show/Hide Columns</p>
        </div>
        {defaultColumns.map(column => (
          <MenuItem
            key={column.id}
            onClick={() => handleColumnToggle(column.id)}
            disabled={column.required}
            sx={{ fontSize: "0.875rem" }}>
            <Checkbox
              checked={visibleColumns[column.id]}
              disabled={column.required}
              size="small"
              sx={{
                color: "#5652B0",
                "&.Mui-checked": {
                  color: "#5652B0",
                },
              }}
            />
            {column.label}
          </MenuItem>
        ))}
      </Menu>

      <UnitTypeModal
        open={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setTimeout(() => {
            setSelectedType(null);
          }, 100);
        }}
        selectedType={selectedType}
        onSelectType={setSelectedType}
      />
    </div>
  );
};

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default Units;
